import type {
	BrowserMetricsConfig,
	GlobalEventTypeConfig,
	WebVitalsGlobalEventTypeConfig,
	GenericGlobalEventTypeConfig,
	PageSegmentLoadGlobalEventTypeConfig,
	PageLoadGlobalEventTypeConfig,
	CustomPlugin,
} from '@atlassian/browser-metrics/types';
import { BMEventsType, PageVisibleFields } from '@atlassian/browser-metrics/types';

import { getBuildInfo } from '@confluence/build-info';
import { Cookies } from '@confluence/cookies';

import { customEventPlugin, pageLoadEventPlugin, webVitalsEventPlugin } from '../plugins';
import { transformObjectToFlattenCustomValuesType } from '../utils/transformObjectToFlattenCustomValuesType';
import { ssrRegionPlugin } from '../plugins/ssrRegionPlugin';
import { getLighthouseMetrics } from '../plugins/lighthouse-metrics';

// used for page-load and page-segment-load events
const ALLOWLIST_FFS = [
	'confluence.frontend.delay.page.comments.render',
	'confluence.frontend.presentation.mode',
	'confluence.ssr.page-statuses',
	'confluence.frontend.fabric.editor.macro.adf.frontend.render',
	'confluence.frontend.fabric.editor.preload.timeout',
	'confluence.frontend.space-shortcuts.pragmatic-dnd',
	'confluence.frontend.custom-sites.page-header-and-title',
	'confluence.frontend.custom-sites.cards-extension',
	'confluence.ssr.preload.query.timeout',
	'confluence.ssr.render.above.threshold',
	'confluence.ssr.delay.hydration.on.hover',
	'confluence.ssr.remove.preload.space.overview.query',
	'confluence.frontend.content.unified.query.migration',
	'confluence.frontend.legacy.bridge.query.migration_0f25n',
	'platform.analytics-next-use-modern-context_fqgbx',
	'confluence.ssr.remove.preload.smartlinks.best.effort',
];

// In order to reduce BM event payload size, include a FF in the BM payload only if it's in the allow list.
// The following FFs will be included in all types of BM event.
const ALLOWLIST_FFS_CUSTOM_EVENTS = [
	'confluence.frontend.pollinator.synthetic.check.tenants',
	'confluence.ssr.delay.hydration.on.hover',
];

/**
 * Returns event configurations that add Confluence specific data.
 *
 * @param sessionData
 */
export const getCustomEventConfigs = (sessionData: any): BrowserMetricsConfig['events'] => {
	const { cloudId, environment, shard, featureFlags } = sessionData;
	const plugins: CustomPlugin[] = [];

	const uniqueIdentifier = featureFlags['confluence.frontend.bm.unique-identifier']
		? Cookies.get('bmUniqueIdentifier')
		: undefined;

	plugins.push(getLighthouseMetrics);

	/**
	 * https://product-fabric.atlassian.net/browse/CCP-1320
	 * Remove unused attributes in page-segment-load event
	 */
	const buildInfo = transformObjectToFlattenCustomValuesType({
		buildInfo: getBuildInfo(),
	});

	/**
	 * Data to be added to all events.
	 */
	const allEventConfig: GlobalEventTypeConfig = {
		custom: transformObjectToFlattenCustomValuesType({
			cloudId,
			environment,
			shard,
			ssr: Boolean(window.__SSR_RENDERED__),
			uniqueIdentifier,
		}),
		featureFlags: ALLOWLIST_FFS_CUSTOM_EVENTS,
		sfxEvents: {
			activeTab: PageVisibleFields.pageVisibleState,
		},
		plugins,
	};

	/**
	 * Data to be added to only page-load events.
	 */
	const pageLoadEventConfig: PageLoadGlobalEventTypeConfig = {
		custom: buildInfo,
		plugins: [pageLoadEventPlugin, ssrRegionPlugin],
		histogram: {
			initial: {
				fmp: '1000_2000_2500_3000_4000_5000',
				tti: '2000_2500_3000_3500_4000_5000_6000',
			},
			transition: {
				fmp: '500_1000_1500_2000_2500_3000',
				tti: '1000_1500_2000_2500_3000_4000',
			},
		},
		// page-load type event will include allEventConfig.featureFlags + the following list.
		featureFlags: ALLOWLIST_FFS,
	};

	/**
	 * Data to be added to only custom events.
	 */
	const pageSegmentLoadEventConfig: PageSegmentLoadGlobalEventTypeConfig = {
		plugins: [customEventPlugin, ssrRegionPlugin],
		histogram: {
			initial: {
				fmp: '1000_2000_2500_3000_4000_5000',
				tti: '2000_2500_3000_3500_4000_5000_6000',
			},
			transition: {
				fmp: '500_1000_1500_2000_2500_3000',
				tti: '1000_1500_2000_2500_3000_4000',
			},
		},
		// page-segment-load type event will include allEventConfig.featureFlags + the following list.
		featureFlags: ALLOWLIST_FFS,
		enableBundleEvalTimings: false,
	};

	const customEventConfig: GenericGlobalEventTypeConfig = {
		custom: buildInfo,
		plugins: [customEventPlugin],
	};

	const webVitalsConfig: WebVitalsGlobalEventTypeConfig = {
		custom: buildInfo,
		enabled: true,
		plugins: [webVitalsEventPlugin],
		// Undefined timeout defaults to 30000ms, increasing timeout here on Cypress tests to prevent flakiness.
		timeout: window?.Cypress ? undefined : 10000,
		ttciEnabled: false,
		featureFlags: ALLOWLIST_FFS,
	};

	return {
		all: allEventConfig,
		[BMEventsType.PAGE_LOAD]: pageLoadEventConfig,
		[BMEventsType.PAGE_SEGMENT_LOAD]: pageSegmentLoadEventConfig,
		[BMEventsType.CUSTOM]: customEventConfig,
		[BMEventsType.WEB_VITALS]: webVitalsConfig,
	};
};
